// Business Settings data layer (Phase 4, P4-WP8) — ONE row per company.
//
// The SINGLE interface between the app and public.business_settings
// (supabase/migrations/20260701195725_business_settings.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • company_id is the PRIMARY KEY, forced by set_company_from_auth and immutable.
//     We NEVER send it — the trigger owns it. RLS scopes reads/writes to the company.
//
// SHAPE: this is a single settings record, not a collection —
//   getBusinessSettings() → row | null   ·   saveBusinessSettings(payload) → company_id
//   (upsert: insert on first save, update thereafter; race-safe on first creation).

import { supabase } from "./supabaseClient";

const T = "business_settings";

export const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_7", label: "Net 7 days" },
  { value: "net_14", label: "Net 14 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
];
export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];
export const VAT_RATES = [0, 9, 13.5, 23];

// ⚠️ THIS ARRAY IS A SILENT FILTER, NOT A VALIDATOR. saveBusinessSettings() runs
// pick(payload, WRITE_FIELDS), so ANY field absent from this list is DROPPED with
// no error, no warning and a successful-looking save. A caller that adds a new
// column to its payload and forgets to add it here gets a green save and a
// database that never changed. There is a regression test named for exactly that
// (src/lib/businessSettings.test.js).
const WRITE_FIELDS = [
  "legal_name", "trading_name", "registration_number", "vat_number",
  "email", "phone", "website",
  "address_line1", "address_line2", "city", "county", "postal_code", "country",
  "logo_url",
  "bank_account_name", "bank_name", "iban", "bic", "sort_code", "account_number",
  "default_payment_terms", "default_currency", "default_vat_rate", "default_terms_text", "invoice_footer",
  // LT-4a. Added so the onboarding wizard can record HOW the business accounts
  // for VAT. The column has existed since CC26c-0 (text not null default
  // 'invoice', CHECK in ('invoice','cash')) and was writable from nowhere in the
  // app for a month — the value was correct only because nobody could change it.
  // ⛔ ONLY 'invoice' OR 'cash' PASS THE CHECK. The wizard never shows the word
  // "basis" to a user; it asks when they account for VAT and maps the answer.
  "vat_basis",
];

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "Business settings already exist for this company."; break;
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You must belong to a company to save business settings."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/**
 * The company's settings row, or null if it hasn't been created yet.
 * @returns {Promise<Object|null>}
 */
export async function getBusinessSettings() {
  const { data, error } = await client().from(T).select("*").limit(1).maybeSingle();
  if (error) throw toError(error, "Could not load business settings");
  return data || null;
}

/**
 * Upsert the company's settings — insert on first save, update thereafter.
 * Race-safe: a concurrent first-insert (unique/PK 23505) is retried as an update.
 * @param {Object} payload
 * @returns {Promise<string>} the company_id.
 */
export async function saveBusinessSettings(payload) {
  const c = client();
  const row = pick(payload || {}, WRITE_FIELDS);
  const existing = await getBusinessSettings();
  if (existing) {
    const { data, error } = await c.from(T).update(row).eq("company_id", existing.company_id).select("company_id").single();
    if (error) throw toError(error, "Could not save business settings");
    return data.company_id;
  }
  const { data, error } = await c.from(T).insert(row).select("company_id").single(); // company_id set by trigger
  if (error) {
    if (error.code === "23505") { // someone created it first → update instead
      const again = await getBusinessSettings();
      if (again) {
        const { data: upd, error: uErr } = await c.from(T).update(row).eq("company_id", again.company_id).select("company_id").single();
        if (uErr) throw toError(uErr, "Could not save business settings");
        return upd.company_id;
      }
    }
    throw toError(error, "Could not save business settings");
  }
  return data.company_id;
}

// ── Demo-data manifest (P4-Onboarding) ───────────────────────────────────────
// The ids of sample/demo data seeded by sampleData.js, stored on the company's
// settings row so "Clear demo data" can tear it down. Company-scoped via RLS.

/** The company's demo manifest, or null if none. */
export async function getDemoManifest() {
  const { data, error } = await client().from(T).select("demo_manifest").limit(1).maybeSingle();
  if (error) throw toError(error, "Could not load the demo manifest");
  return data?.demo_manifest || null;
}

/** Persist (or clear, with null) the demo manifest. Upserts the settings row if absent. */
export async function setDemoManifest(manifest) {
  const c = client();
  const existing = await getBusinessSettings();
  if (existing) {
    const { data, error } = await c.from(T).update({ demo_manifest: manifest }).eq("company_id", existing.company_id).select("company_id").single();
    if (error) throw toError(error, "Could not save the demo manifest");
    return data.company_id;
  }
  const { data, error } = await c.from(T).insert({ demo_manifest: manifest }).select("company_id").single(); // company_id set by trigger
  if (error) throw toError(error, "Could not save the demo manifest");
  return data.company_id;
}
